import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');

      if (token) {
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Verify token and get user data
        const response = await axios.get('/api/auth/me');
        if (response.data.success) {
          setUser(response.data.data);
        } else {
          // Invalid token, remove it
          localStorage.removeItem('authToken');
          delete axios.defaults.headers.common['Authorization'];
        }
      } else {
        // Generate session ID for guest users
        let guestSessionId = localStorage.getItem('guestSessionId');
        if (!guestSessionId) {
          guestSessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('guestSessionId', guestSessionId);
        }
        setSessionId(guestSessionId);

        // Set session ID header for guest users
        axios.defaults.headers.common['X-Session-Id'] = guestSessionId;
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Clear invalid token
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];

      // Ensure guest session ID is set
      let guestSessionId = localStorage.getItem('guestSessionId');
      if (!guestSessionId) {
        guestSessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('guestSessionId', guestSessionId);
      }
      setSessionId(guestSessionId);
      axios.defaults.headers.common['X-Session-Id'] = guestSessionId;
    } finally {
      setLoading(false);
    }
  };

  const login = token => {
    localStorage.setItem('authToken', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Clear guest session when user logs in
    localStorage.removeItem('guestSessionId');
    delete axios.defaults.headers.common['X-Session-Id'];
    setSessionId(null);

    // Fetch user data
    initializeAuth();
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);

    // Set up guest session
    const guestSessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('guestSessionId', guestSessionId);
    setSessionId(guestSessionId);
    axios.defaults.headers.common['X-Session-Id'] = guestSessionId;
  };

  const value = {
    user,
    sessionId,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isGuest: !user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
